import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaClock, FaLock, FaUnlock, FaRobot } from 'react-icons/fa';
import { formatDate } from '../utils/dateFormatter';
import { useCredits } from '../contexts/CreditsContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import InsufficientCreditsModal from './credits/InsufficientCreditsModal';
import ContentShareButton from './ContentShareButton';
import API_CONFIG from '../config/api';
import './PostCard.css';

const buildPostPublicPath = (post) => {
    const page_slug = post.page_slug;
    const slug =
        post.slug ||
        (post.title
            ? post.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
            : '') ||
        `post-${post.page_id || post.id || ''}`;
    if (page_slug && slug) {
        return `/post/${page_slug}/${slug}`;
    }
    return null;
};

const PostCard = ({ post, onPostClick }) => {
    const { creditBalance, hasSufficientCredits } = useCredits();
    const { openLoginModal } = useAuthModal();
    const navigate = useNavigate();
    const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
    const [imageError, setImageError] = useState(false);

    const title = post.title || 'Untitled Post';
    const description = post.description || post.summary || post.excerpt || '';
    const publishDate = post.published_at || post.created_at || new Date().toISOString();
    const formattedDate = formatDate(publishDate);

    const imageServer = API_CONFIG.BASE_URL;
    const coverImageUrl = post.cover_image_url ? `${imageServer}${post.cover_image_url}` : null;

    const getContentPreview = () => {
        let textSource = description;

        if ((!textSource || textSource.length < 30) && post.content) {
            if (Array.isArray(post.content)) {
                for (let i = 0; i < post.content.length; i++) {
                    const item = post.content[i];
                    if (item.content && typeof item.content === 'string' && item.content.trim().length > 0) {
                        textSource = item.content;
                        break;
                    } else if (item.body && typeof item.body === 'string' && item.body.trim().length > 0) {
                        textSource = item.body;
                        break;
                    }
                }
            } else if (typeof post.content === 'string') {
                textSource = post.content;
            }
        }

        if (!textSource || textSource.trim().length === 0) {
            return `Explore this post about ${title.toLowerCase()}. Click to read the full content and learn more about this topic.`;
        }
        textSource = textSource.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        const words = textSource.split(' ');
        const preview = words.slice(0, 30).join(' ');
        return words.length > 30 ? preview + '...' : preview;
    };

    const contentPreview = getContentPreview();

    const checkCreditAccess = () => {
        if (!post.is_paid) return true;
        const postPrice = post.price || 1;
        return hasSufficientCredits(postPrice);
    };

    const handleCloseInsufficientCreditsModal = () => {
        setShowInsufficientCreditsModal(false);
    };

    const handleImageError = () => {
        setImageError(true);
    };

    const getPostShareUrl = () => {
        const path = buildPostPublicPath(post);
        if (path) {
            return `${window.location.origin}${path}`;
        }
        return window.location.origin;
    };

    const handleCardClick = (e) => {
        e.preventDefault();

        if (onPostClick) {
            onPostClick(post);
            return;
        }
        if (post.is_paid && !checkCreditAccess()) {
            setShowInsufficientCreditsModal(true);
            return;
        }
        const previewPath = buildPostPublicPath(post);
        if (previewPath) {
            navigate(previewPath);
            return;
        }
        if (post.id) {
            navigate(`/post/${post.id}`);
        }
    };

    return (
        <div className="post-card">
            <div className="card-link" onClick={handleCardClick} role="presentation">
                <div className="card-cover-image">
                    {coverImageUrl && !imageError ? (
                        <>
                            {post.is_paid ? (
                                <div className="access-tag premium"><FaLock /> PREMIUM CONTENT</div>
                            ) : (
                                <div className="access-tag free"><FaUnlock /> FREE ACCESS</div>
                            )}
                            <img
                                src={coverImageUrl}
                                alt={title}
                                onError={handleImageError}
                                loading="lazy"
                            />
                        </>
                    ) : (
                        <div className="card-cover-image-placeholder" aria-hidden="true" />
                    )}
                </div>

                <div className="card-content">
                    <div className="card-title-wrapper">
                        <h3 className="card-title">{title}</h3>
                    </div>
                    {contentPreview && (
                        <div className="content-preview">
                            <p>
                                {contentPreview}{' '}
                                <span
                                    className="read-more"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (post.is_paid && !checkCreditAccess()) {
                                            setShowInsufficientCreditsModal(true);
                                            return;
                                        }
                                        if (post.id) {
                                            navigate(`/post/${post.id}`);
                                            return;
                                        }
                                        const page_slug = post.page_slug;
                                        const slug =
                                            post.slug ||
                                            (post.title ? post.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : '') ||
                                            `post-${post.page_id || post.id || Math.floor(Math.random() * 1000)}`;
                                        navigate(`/post/${page_slug}/${slug}`);
                                    }}
                                ></span>
                            </p>
                        </div>
                    )}
                    <div className="card-footer">
                        <div className="card-meta-right">
                            {Number.isFinite(Number(post.ai_score)) && Number(post.ai_score) >= 0 && (
                                <span className="ai-score-badge" title={`AI Score: ${post.ai_score}/5`}>
                                    <FaRobot /> {post.ai_score}/5
                                </span>
                            )}
                            <span className="publish-date">
                                <FaClock className="date-icon" /> {formattedDate}
                            </span>
                        </div>
                        <ContentShareButton
                            shareUrl={getPostShareUrl()}
                            title={title}
                            type="post"
                            className="post-card-share-btn"
                        />
                    </div>
                </div>
            </div>

            <InsufficientCreditsModal
                isOpen={showInsufficientCreditsModal}
                onClose={handleCloseInsufficientCreditsModal}
                requiredCredits={post.price || 1}
                currentCredits={creditBalance}
                contentTitle={post.title || 'this premium content'}
                contentType="post"
                onLoginRequired={openLoginModal}
            />
        </div>
    );
};

export default PostCard;
